import { defineStore } from "pinia";
import type { User, LoginResponse } from "~/types";

function safeLocalStorage(): Storage | null {
  try { return typeof localStorage !== "undefined" ? localStorage : null; } catch { return null; }
}

function storagePrefix(): string {
  if (import.meta.server) return "clinic_"
  const host = window.location.hostname
  const path = window.location.pathname
  const isPatient = host.startsWith("paciente.")
    || path.startsWith("/patient")
    || path.startsWith("/auth/login-patient")
  return isPatient ? "patient_" : "clinic_"
}

interface ClinicOption {
  schema: string;
  slug: string;
  name: string;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  tenantSlug: string | null;
  clinicName: string | null;
  loading: boolean;
  pendingClinics: ClinicOption[];
  pendingTempToken: string | null;
}

export const useAuthStore = defineStore("auth", {
  state: (): AuthState => ({
    user: null,
    accessToken: null,
    refreshToken: null,
    tenantSlug: null,
    clinicName: null,
    loading: false,
    pendingClinics: [],
    pendingTempToken: null,
  }),

  getters: {
    isAuthenticated: (state) => !!state.accessToken,
    isAdmin: (state) => state.user?.role === "admin",
    isProfessional: (state) => state.user?.role === "professional",
    fullName: (state) => {
      if (!state.user) return "";
      const name = `${state.user.first_name ?? ''} ${state.user.last_name ?? ''}`.trim();
      if (!name || name.includes('@')) return state.user.email.split('@')[0];
      return name;
    },
  },

  actions: {
    async login(email: string, password: string): Promise<{
      requires_2fa: boolean;
      requires_clinic_selection?: boolean;
      temp_token?: string;
      email?: string;
    }> {
      this.loading = true;
      try {
        const api = useApi();
        const { data } = await api.post<LoginResponse>("/auth/login", { email, password });
        const payload = data.data;
        if (payload?.requires_2fa) {
          return { requires_2fa: true, temp_token: payload.temp_token, email: payload.email };
        }
        if (payload?.requires_clinic_selection) {
          this.setPendingClinicSelection(payload.clinics ?? [], payload.temp_token ?? '');
          return { requires_2fa: false, requires_clinic_selection: true };
        }
        this.setTokens(payload.tokens!);
        this.user = payload.user!;
        // Sincroniza role com o JWT (pode diferir do UserSerializer quando o user
        // tem roles diferentes em clínicas distintas — o JWT reflete o role da clínica atual)
        try {
          const jwtParts = payload.tokens!.access.split('.');
          const jwtPayload = JSON.parse(atob(jwtParts[1].replace(/-/g, '+').replace(/_/g, '/')));
          if (this.user && jwtPayload?.role) this.user = { ...this.user, role: jwtPayload.role };
        } catch {}
        this._saveUser();
        return { requires_2fa: false, requires_clinic_selection: false };
      } finally {
        this.loading = false;
      }
    },

    setPendingClinicSelection(clinics: ClinicOption[], tempToken: string) {
      this.pendingClinics = clinics;
      this.pendingTempToken = tempToken;
      const storage = safeLocalStorage();
      if (storage) {
        const pfx = storagePrefix();
        storage.setItem(`${pfx}pendingTempToken`, tempToken);
        storage.setItem(`${pfx}pendingClinics`, JSON.stringify(clinics));
      }
    },

    async selectClinic(schema: string) {
      this.loading = true;
      try {
        const api = useApi();
        const { data } = await api.post("/auth/select-clinic", {
          temp_token: this.pendingTempToken,
          schema,
        });
        const payload = data.data;
        this.setTokens(payload.tokens);
        this.user = payload.user;
        this._saveUser();
        const clinic = this.pendingClinics.find(c => c.schema === schema);
        if (clinic) {
          this.clinicName = clinic.name;
          const storage = safeLocalStorage();
          if (storage) {
            const pfx = storagePrefix()
            storage.setItem(`${pfx}clinicName`, clinic.name);
            storage.setItem(`${pfx}clinicList`, JSON.stringify(this.pendingClinics));
          }
        }
        this.pendingTempToken = null;
        const storage = safeLocalStorage();
        if (storage) {
          const pfx = storagePrefix();
          storage.removeItem(`${pfx}pendingTempToken`);
          storage.removeItem(`${pfx}pendingClinics`);
        }
      } finally {
        this.loading = false;
      }
    },

    async verify2FA(tempToken: string, code: string): Promise<{ requires_clinic_selection: boolean }> {
      this.loading = true;
      try {
        const api = useApi();
        const { data } = await api.post("/auth/verify-2fa", { temp_token: tempToken, code });
        const payload = data.data;
        if (payload?.requires_clinic_selection) {
          this.pendingClinics = payload.clinics ?? [];
          this.pendingTempToken = payload.temp_token ?? null;
          return { requires_clinic_selection: true };
        }
        this.setTokens(payload.tokens);
        this.user = payload.user;
        this._saveUser();
        return { requires_clinic_selection: false };
      } finally {
        this.loading = false;
      }
    },

    async fetchMe(): Promise<User> {
      const api = useApi();
      const { data } = await api.get("/auth/me");
      this.user = data.data;
      return data.data;
    },

    async refresh() {
      if (!this.refreshToken) throw new Error("No refresh token");
      const api = useApi();
      const { data } = await api.post("/auth/refresh", { refresh: this.refreshToken });
      this.setTokens(data.data);
    },

    async logout() {
      try {
        const push = usePushNotifications();
        await push.unregister();
      } catch {}
      try {
        const api = useApi();
        await api.post("/auth/logout", { refresh: this.refreshToken });
      } catch { /* ignore */ }
      this.clearAuth();
    },

    setTokens(tokens: { access: string; refresh: string }) {
      this.accessToken = tokens.access;
      this.refreshToken = tokens.refresh;
      const storage = safeLocalStorage();
      if (storage) {
        const pfx = storagePrefix()
        storage.setItem(`${pfx}accessToken`, tokens.access);
        storage.setItem(`${pfx}refreshToken`, tokens.refresh);
      }
    },

    _saveUser() {
      const storage = safeLocalStorage();
      if (storage && this.user) {
        const pfx = storagePrefix()
        storage.setItem(`${pfx}user`, JSON.stringify(this.user));
      }
    },

    setClinicName(name: string) {
      this.clinicName = name;
      const storage = safeLocalStorage();
      if (storage) storage.setItem(`${storagePrefix()}clinicName`, name);
    },

    setTenant(slug: string) {
      this.tenantSlug = slug;
      const storage = safeLocalStorage();
      if (storage) storage.setItem(`${storagePrefix()}tenantSlug`, slug);
    },

    initFromStorage() {
      const storage = safeLocalStorage();
      if (storage) {
        const pfx = storagePrefix()
        const access = storage.getItem(`${pfx}accessToken`);
        const refresh = storage.getItem(`${pfx}refreshToken`);
        if (access && refresh) {
          this.accessToken = access;
          this.refreshToken = refresh;
        }
        const raw = storage.getItem(`${pfx}user`);
        if (raw) {
          try { this.user = JSON.parse(raw); } catch { /* ignore */ }
        }
        const slug = storage.getItem(`${pfx}tenantSlug`);
        if (slug) this.tenantSlug = slug;
        const name = storage.getItem(`${pfx}clinicName`);
        if (name) this.clinicName = name;
        const list = storage.getItem(`${pfx}clinicList`);
        if (list) {
          try { this.pendingClinics = JSON.parse(list); } catch { /* ignore */ }
        }
        const pendingClinicsRaw = storage.getItem(`${pfx}pendingClinics`);
        if (pendingClinicsRaw) {
          try { this.pendingClinics = JSON.parse(pendingClinicsRaw); } catch { /* ignore */ }
        }
        const pendingToken = storage.getItem(`${pfx}pendingTempToken`);
        if (pendingToken) this.pendingTempToken = pendingToken;
      }
    },

    clearAuth() {
      this.user = null;
      this.accessToken = null;
      this.refreshToken = null;
      this.tenantSlug = null;
      this.clinicName = null;
      this.pendingClinics = [];
      this.pendingTempToken = null;
      const storage = safeLocalStorage();
      if (storage) {
        for (const pfx of ["clinic_", "patient_"]) {
          storage.removeItem(`${pfx}accessToken`);
          storage.removeItem(`${pfx}refreshToken`);
          storage.removeItem(`${pfx}tenantSlug`);
          storage.removeItem(`${pfx}clinicName`);
          storage.removeItem(`${pfx}clinicList`);
        }
        // No mobile, reseta o endpoint da clínica para o público de descoberta
        storage.removeItem('clinic_api_base');
      }
    },
  },
});
